// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ThresholdSettings.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ThresholdSettings Component
 */

import 'share/utils/Izenda.StringUtilities';
import React, { Component } from 'react';
import { invokeArgs, head, get, isString, find, forEach, isNil, groupBy, map } from 'lodash/fp';
import ChartSettingsContainer from './Izenda.ChartSettingsContainer';
import { parser as chartDataParser } from 'share/utils/Izenda.ChartDataParser';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import localizer from 'share/localization/Izenda.Localizer';
import { observer } from 'mobx-react';
import { ContainerName, isChartType } from 'logic/data/Izenda.ReportPartChartConstants';
import { observable } from 'mobx';

const VALUE_LABEL_METRICS_CHART = [CHART_TYPE.HeatMap, CHART_TYPE.Bubble, CHART_TYPE.Scatter];
const OTHER_PROPS_THRESHOLD_KEY = 'threshold';

/**
 * Check chart has support all metric options
 * @param {String} chartType
 */
const hasSupportAllMetric = (chartType: string): boolean =>
  !isChartType(chartType, CHART_TYPE.Scatter, CHART_TYPE.Bubble, CHART_TYPE.Combination, CHART_TYPE.Waterfall);

/**
 * Get schema
 * @param {Object} xOption
 * @param {Object} yOption
 * @param {Array<any>} dataTree
 * @param {Object} reportPartContent
 * @param {Object} fieldStore
 * @param {Object} reportPartDetails
 */
function getSchema(
  xOption: any,
  yOption: any,
  dataTree: Array<any>,
  reportPartContent: any,
  fieldStore: any,
  reportPartDetails: any
) {
  const chartType = reportPartDetails.reportPartContent.properties.chartType;
  return {
    schemaKey: 'THRESHOLD_SETTINGS_SCHEMA',
    fields: {
      xAxisTitle: {
        title: localizer.getText('THREHOSLD_X_LABEL'),
        type: 'static',
        isHidden: 'isHiddenXAxis'
      },
      xAxis: {
        component: require('./Izenda.DynamicThresholdGrid'),
        fnValue: 'getXAxis',
        props: {
          staticThreshold: true,
          axis: 'xAxis',
          selectedAxis: 'getXAxisSelectedOption',
          dataType: 'getXAxisDataType',
          options: xOption,
          chartType
        },
        isHidden: 'isHiddenXAxis'
      },
      yAxisTitle: {
        title: localizer.getText('THREHOSLD_Y_LABEL'),
        type: 'static',
        isHidden: 'isHiddenYAxis'
      },
      yAxis: {
        component: require('./Izenda.DynamicThresholdGrid'),
        fnValue: 'getYAxis',
        props: {
          selectedAxis: 'getYAxisSelectedOption',
          axis: 'yAxis',
          dataType: 'getYAxisDataType',
          options: yOption,
          dataTree,
          reportPartContent,
          fieldStore,
          reportPartDetails,
          chartType
        },
        isHidden: 'isHiddenYAxis'
      }
    }
  };
}

@observer
export default class ThresholdSettings extends Component {
  @observable metricAllPlotLinesOption;
  constructor(props) {
    super(props);

    this.cloneElements();
    this.initData();
    const value = props.value || {};
    const { xAxisTitleOption: xOption, yAxisTitleOption: yOption } = value;

    this.state = {
      selectedXAxisOption: xOption.length > 0 && head(xOption),
      selectedYAxisOption: yOption.length > 0 && head(yOption)
    };
  }

  /**
   * Get value when this popup is closed
   */
  getValue() {
    const listDynamicField = [];
    this.updateElements('labelsElements', listDynamicField);
    this.updateElements('valuesElements', listDynamicField);

    const dyncContainer = this.reportPartDetails.reportPartContent.customContainers.find(con => con.name === ContainerName.DYNAMIC_THRESHOLD);
    // Update threholds field in customContainers
    this.props.reportPartDetails.reportPartContent.updateDynamicThresholdFieldInContainer(
      dyncContainer.elements
    );

    forEach(item => {
      const matchField = find(
        ['id', item.izValue],
        this.props.fieldStore.dataTree.flattenQuerySourceIncludeCalculatedField
      );
      !isNil(matchField) && listDynamicField.push(matchField);
    }, this.allDynamicThresholdLine);

    return {
      metricAllPlotLinesOption: this.metricAllPlotLinesOption,
      dynamicThresholdField: listDynamicField,
      allDynamicThresholdLine: this.allDynamicThresholdLine
    };
  }

  /**
   * Clone elements
   */
  cloneElements() {
    this.reportPartDetails = this.props.reportPartDetails.clone();
    this.reportPartDetails.colorPalettes = this.props.reportPartDetails.colorPalettes;
  }

  /**
   * Update elements
   * @param {string} type
   */
  updateElements(type: string, listDynamicField) {
    const reportPartContent = this.reportPartDetails.reportPartContent;
    const content = this.props.reportPartDetails.reportPartContent;

    content[type].forEach((item, index) => {
      const newValue = invokeArgs(
        'properties.getOtherProps',
        [OTHER_PROPS_THRESHOLD_KEY],
        reportPartContent[type][index]
      );
      item.setOtherProps(OTHER_PROPS_THRESHOLD_KEY, newValue);
      const dynamic = invokeArgs(
        'properties.getOtherProps',
        [ContainerName.DYNAMIC_THRESHOLD],
        reportPartContent[type][index]
      );
      item.setOtherProps(ContainerName.DYNAMIC_THRESHOLD, dynamic);
      if (!isNil(dynamic)) {
        const dataTree = this.props.fieldStore.dataTree.flattenQuerySourceIncludeCalculatedField;
        forEach(item => {
          const matchField = find(['id', item.izValue], dataTree);
          !isNil(matchField) && listDynamicField.push(matchField);
        }, dynamic);
      }
    });
  }

  /**
   * Init data
   * Store drilldown data to this instance
   */
  initData() {
    const props = this.props;
    const value = props.value || {};
    const reportPartDetails = this.reportPartDetails;
    this.activeSeriesKey = null;
    this.metricAllPlotLinesOption = value.metricAllPlotLinesOption;
    this.allDynamicThresholdLine = value.allDynamicThresholdLine;
    this.expandedLevel = 0;

    this.previewData = reportPartDetails.previewData;
    this.previewDataRecords = reportPartDetails.previewDataRecords || [];
    this.previewDataFieldsMapping = reportPartDetails.previewDataFieldsMapping || [];
  }

  /**
   * Get chart data
   */
  getChartData() {
    const reportPartDetails = this.reportPartDetails;
    const reportPartContent = reportPartDetails.reportPartContent;
    const chartType = reportPartContent.chartType;
    let key = isChartType(chartType, CHART_TYPE.Combination, CHART_TYPE.Sparkline) ? null : this.activeSeriesKey;
    key = isString(key) ? key : key && key.name;
    return chartDataParser(reportPartContent.getActiveElmsByActiveSeries(key), this.expandedLevel, {
      fieldsMapping: this.previewDataFieldsMapping,
      records: this.previewDataRecords,
      rawData: this.previewData
    });
  }

  /**
   * Get configuration definition
   */
  getConfigurationDef() {
    const chartType = get('reportPartContent.chartType', this.props.reportPartDetails);
    const props = this.props;
    const supportALlOptions = hasSupportAllMetric(chartType);

    const metricAllPlotLinesOption = supportALlOptions ? this.metricAllPlotLinesOption || [] : [];

    const allDynamicThresholdLine = supportALlOptions ? this.allDynamicThresholdLine || [] : [];
    const value = props.value || {};
    const reportPartContent = props.reportPartDetails.reportPartContent;

    const xOption = value.xAxisTitleOption || [];
    const yOption = value.yAxisTitleOption || [];

    const { selectedXAxisOption, selectedYAxisOption } = this.state;

    const xField = find({ fieldName: selectedXAxisOption.value }, reportPartContent.labelsElements);
    this.xField = xField;
    const yField = find({ fieldName: selectedYAxisOption.value }, reportPartContent.valuesElements);
    const dataTree = this.props.fieldStore.dataTree.flattenQuerySourceIncludeCalculatedField;
    const dyncContainer = this.reportPartDetails.reportPartContent.customContainers.find(con => con.name === ContainerName.DYNAMIC_THRESHOLD);

    this.yField = yField;
    return {
      definedSchema: getSchema(
        xOption,
        yOption,
        dataTree,
        reportPartContent,
        this.props.fieldStore,
        this.reportPartDetails
      ),
      source: {},
      onChangeValue: this.onChangeValue,
      mappingProps: {
        beforeChangeAxisTitle: () => {
          return props.validation.validate().then(result => {
            return result ? Promise.resolve() : Promise.reject(new Error('Validation Fail.'));
          });
        }
      },
      mappingSource: {
        getXAxis: () => {
          let thresholds = [];
          forEach(item => {
            const threshold = item.getValueByKey(OTHER_PROPS_THRESHOLD_KEY) || [];

            // sync new value of metric
            forEach(thresholdItem => {
              thresholdItem.fieldMappingAlias = item.fieldNameAlias;
            }, threshold);

            thresholds = thresholds.concat(threshold.slice());
          }, reportPartContent.labelsElements);

          return thresholds;
        },
        getYAxis: () => {
          let thresholds = metricAllPlotLinesOption || [];

          forEach(thresholdItem => {
            const thresholdFieldElement = find(
              { fieldName: thresholdItem.fieldName },
              dyncContainer.elements
            );
            thresholdItem.thresholdFieldElement = thresholdFieldElement;
          }, allDynamicThresholdLine);

          thresholds = thresholds.concat(allDynamicThresholdLine);

          forEach(item => {
            const threshold = item.getValueByKey(OTHER_PROPS_THRESHOLD_KEY) || [];

            const dynamic = !isChartType(chartType, CHART_TYPE.Scatter, CHART_TYPE.Bubble)
              ? item.getValueByKey(ContainerName.DYNAMIC_THRESHOLD) || []
              : [];

            forEach(thresholdItem => {
              const thresholdFieldElement = find(
                { fieldName: thresholdItem.fieldName },
                dyncContainer.elements
              );
              thresholdItem.thresholdFieldElement = thresholdFieldElement;
            }, dynamic);

            forEach(thresholdItem => {
              thresholdItem.fieldMappingAlias = item.fieldNameAlias;
            }, dynamic);

            // sync new value of metric
            forEach(thresholdItem => {
              thresholdItem.fieldMappingAlias = item.fieldNameAlias;
            }, threshold);

            thresholds = thresholds.concat(threshold.slice());
            thresholds = thresholds.concat(dynamic.slice());
          }, value.valuesElements);
          return thresholds;
        },

        getXAxisSelectedOption: () => selectedXAxisOption,
        getXAxisSelectedOptionValue: () => get('value', selectedXAxisOption),
        getYAxisSelectedOption: () => selectedYAxisOption,
        getYAxisSelectedOptionValue: () => get('value', selectedYAxisOption),
        isHiddenXAxis: () => reportPartContent.listLinearFieldInLabelContainer.length === 0,
        isHiddenYAxis: () => reportPartContent.listCountableFieldInValueContainer.length === 0,
        getXAxisDataType: () => xField && xField.previewDataType,
        getYAxisDataType: () => yField && yField.previewDataType
      }
    };
  }

  render() {
    const reportPartDetails = this.reportPartDetails;
    const reportPartContent = reportPartDetails.reportPartContent;

    const getProperties = key => reportPartContent.getReportPartProperties(key);

    const chartProperties = getProperties();
    const newData = {
      chartOptions: { ...chartProperties, colors: reportPartDetails.getThemeColors(chartProperties) },
      seriesOptions: reportPartDetails.fieldCollection,
      fieldsMapping: reportPartDetails.previewDataFieldsMapping,
      chartData: chartDataParser(reportPartContent.activeElmsByContainers, reportPartDetails.expandedLevel, {
        fieldsMapping: reportPartDetails.previewDataFieldsMapping,
        records: reportPartDetails.previewDataRecords,
        rawData: reportPartDetails.previewData
      })
    };

    const chartOptions = {
      commonXYAxis: {
        metricAllPlotLinesOption: this.metricAllPlotLinesOption,
        allDynamicThresholdLine: this.allDynamicThresholdLine
      }
    };

    return (
      <ChartSettingsContainer
        isLoading={reportPartDetails.isLoadingData}
        className='threshold-settings'
        type={this.props.reportPartType}
        configurationDef={this.getConfigurationDef()}
        chartOptions={chartOptions}
        allChartProps={newData}
        chartData={newData.chartData}
      />
    );
  }

  /**
   * Handle changed value of threshold setting
   * @param {Object} schemaData
   * @param {string} changedKey
   * @param {Array<string>} changedKeyPath
   * @param {Object} changedValue
   */
  onChangeValue = (schemaData: any, changedKey: string, changedKeyPath: any, changedValue: any) => {
    const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
    const fieldData = groupData[changedKey];
    const reportPartContent = this.reportPartDetails.reportPartContent;
    fieldData && (fieldData.value = changedValue);

    const value = this.props.value;
    const { yAxisTitle } = schemaData;
    const { xAxisTitleOption: xOption, yAxisTitleOption: yOption } = value;

    // set active series key, if the charType's field container is values container
    const chartType = get('reportPartContent.chartType', this.props.reportPartDetails);
    const chartTypeSupportAll = hasSupportAllMetric(chartType);

    !isChartType(chartType, ...VALUE_LABEL_METRICS_CHART) && (this.activeSeriesKey = yAxisTitle.value || null);

    if (changedKey === 'xAxis') {
      // this.xField && this.xField.setOtherProps(OTHER_PROPS_THRESHOLD_KEY, schemaData.xAxis.value);
      const staticValueX = groupBy(item => item.fieldMappingAlias, schemaData.xAxis.value);
      forEach(item => {
        const data = map(function(item) {
          return item.exportToChartConfig ? item.exportToChartConfig() : item;
        }, staticValueX[item.fieldName]);

        item.setOtherProps(OTHER_PROPS_THRESHOLD_KEY, data);
      }, reportPartContent.labelsElements);
    } else {
      const valueYGroupBy = groupBy(item => item.thresholdType, schemaData.yAxis.value);
      const staticValue = valueYGroupBy['static'];
      const dynamicValue = valueYGroupBy['dynamic'];
      const staticValueGroupBy = groupBy(item => item.fieldMappingAlias, staticValue);
      const dynamicValueGroupBy = groupBy(item => item.fieldMappingAlias, dynamicValue);
      // dynamic value group by.
      chartTypeSupportAll &&
        (this.metricAllPlotLinesOption = map(function(item) {
          return item.exportToChartConfig ? item.exportToChartConfig() : item;
        }, staticValueGroupBy['']));

      // dynamic value group by.
      chartTypeSupportAll &&
        (this.allDynamicThresholdLine = map(function(item) {
          return item.exportToChartConfig ? item.exportToChartConfig() : item;
        }, dynamicValueGroupBy['']));

      forEach(item => {
        const data = map(function(item) {
          return item.exportToChartConfig ? item.exportToChartConfig() : item;
        }, staticValueGroupBy[item.fieldName]);

        const dynamicValue = map(function(item) {
          return item.exportToChartConfig ? item.exportToChartConfig() : item;
        }, dynamicValueGroupBy[item.fieldName]);

        item.setOtherProps(OTHER_PROPS_THRESHOLD_KEY, data);
        item.setOtherProps('dynamicThresholds', dynamicValue);
      }, reportPartContent.valuesElements);
    }

    updateState.call(this);

    // TODO: remove state on this component
    function updateState() {
      const xAxisDateTimeFormat = find({ value: schemaData.xAxisTitle.value }, xOption) || {};
      const yAxisDateTimeFormat = find({ value: schemaData.yAxisTitle.value }, yOption) || {};

      this.setState({
        selectedXAxisOption: {
          value: schemaData.xAxisTitle.value,
          dateTimeFormatData: xAxisDateTimeFormat.dateTimeFormatData
        },
        selectedYAxisOption: {
          value: schemaData.yAxisTitle.value,
          dateTimeFormatData: yAxisDateTimeFormat.dateTimeFormatData
        }
      });
    }
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ThresholdSettings.js